<template>
    <div>
        <Hero title="Build real-time AI agents with Unified MCP" :highlightWords="['real-time', 'AI', 'agents']"
            :subtitle="`Access authorized SaaS data from ${INTEGRATIONS_COUNT_10s}+ integrations with 20,000+ callable tools inside a LLM API - with no custom integration logic or prompt hacking.`"
            :buttons="[
                { link: 'https://app.unified.to', title: 'Get started free' },
                { link: '/contact', title: 'Request a demo', icon: 'ic:round-keyboard-arrow-right' },
            ]" />

        <section class="mt-20 mb-20 sm:mr-20 sm:ml-20">
            <HeroImage image_url="/images/MCP_V2.png" :image_width="1200" :image_height="482" />
        </section>

        <!-- <McpCode token="YOUR_TOKEN" connection="YOUR_CONNECTION_ID" /> -->

        <section class="mt-20 mb-20">
            <Customers />
        </section>

        <MarginImageComponent title="Building an AI agent or AI-first application?" mcp_code
            subtitle="Unified MCP gives you access to all of your customer's data and allows you to launch today."
            :data="[
                'Unified MCP extends our real-time API infrastructure into the world of LLMs—providing access to your customer\'s third-party data as structured tools inside Anthropic, OpenAI, Google Gemini, and more.',
                'No custom integration logic. Just authorized access to your customer\'s real-time SaaS data, formatted for AI-native execution.',
                'With Unified MCP, your AI product can offer AI-powered actions that act on customer data directly—securely, predictably, and at scale.',
            ]" :button="{
                link: 'https://docs.unified.to/mcp',
                title: 'How it works',
                icon: 'ic:round-keyboard-arrow-right'
            }" />

        <section class="mt-20 mb-20">
            <IntegrationsHero title="Seamlessly and securely access hundreds of integrations"
                :subtitle="`Explore ${INTEGRATIONS_COUNT_10s} pre-built integrations`" subtitle_link="/integrations" />
        </section>

        <Benefits bottom_image title="Make your SaaS data LLM-executable" :benefits="[
            {
                // image_url: '/images/Embedded-Authorization.svg',
                title: 'Real-time access',
                body: 'Every MCP action runs on real-time SaaS data—fetched securely from the source, exactly when the model needs it.',
            },
            {
                // image_url: '/images/Embedded-Sign-up.svg',
                title: 'Bi-directional API actions',
                body: 'Trigger actions like updating a candidate, posting a note, or closing a deal—your LLM can do more than just read.',
            },
            {
                // image_url: '/images/Embedded-Directory.svg',
                title: `${INTEGRATIONS_COUNT_10s}+  integrations`,
                body: 'Unified MCP supports ' + INTEGRATIONS_COUNT_10s + '+ SaaS platforms across 21 categories. so you can expose meaningful tools in every vertical your customers care about.',
            },
        ]" />

        <section class="max-w-screen-xl mx-auto w-full py-12">
            <div class="grid md:grid-cols-2 gap-24 items-start">
                <div class="space-y-6">
                    <h2 class="text-4xl font-bold text-white">One authorization flow, real-time actions for every
                        integration</h2>
                    <p class="text-neutral-50">
                        Unified.to handles authentication, token refresh, and permission scopes—so your LLM agents can
                        securely access customer data across {{ INTEGRATIONS_COUNT_10s }}+ SaaS platforms. Once
                        connected, Unified MCP exposes
                        only what's authorized. Every action reflects real-time, scoped access—no manual wiring or
                        unsafe
                        shortcuts required.
                    </p>

                    <a href="https://docs.unified.to/auth/overview#instructions"
                        class="text-blue-500 flex items-center gap-2">
                        See documentation
                        <Icon name="ic:round-keyboard-arrow-right" />
                    </a>
                </div>
                <div>
                    <AuthCode workspace_id="12345" />
                    <!-- <img src="/images/auth_components.png" alt="Embedded authorization components"
                        class="w-full h-auto object-contain max-h-80" /> -->
                </div>
            </div>
        </section>

        <section class="px-3 lg:w-full sm:py-12 py-32">
            <UseCases title="Fueling agents with real-time tools"
                subtitle="Whether you're building AI agents, predictive workflows, or automated copilots—Unified MCP turns your integrations into structured, callable tools."
                :usecases="[
                    {
                        title: 'Support — Support Resolution Agent',
                        body: 'Reads ticket history, updates ticket status, and triggers actions across support platforms.',
                        category: 'ticketing',
                        button: { link: 'https://unified.to/ticketing', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/magnifying_glass.svg',
                        title: 'AI Recruiter',
                        body: 'Sources, evaluates, and syncs job applicants.',
                        category: 'ATS',
                        button: { link: 'https://unified.to/ats', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/dart.svg',
                        title: 'Lead Scoring',
                        body: 'Auto-classifies and ranks leads in real time.',
                        category: 'CRM',
                        button: { link: 'https://unified.to/crm', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/brain.svg',
                        title: 'Candidate Assessment',
                        body: 'Tests and evaluates candidates, including video interviews, assessments, and background checks.',
                        category: 'ATS',
                        button: { link: 'https://unified.to/ats', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/alert.svg',
                        title: 'Inventory Copilot',
                        body: 'Assess stock shortages and trigger inventory refills.',
                        category: 'E-COMMERCE',
                        button: { link: 'https://unified.to/kms', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/user.svg',
                        title: 'HR Onboarding',
                        body: 'Kicks off onboarding and updates records on role change.',
                        category: 'HR',
                        button: { link: 'https://unified.to/hris', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/math.svg',
                        title: 'Financial Insights',
                        body: 'Generates insights, anomalies, and ROI reporting.',
                        category: 'ACCOUNTING',
                        button: { link: 'https://unified.to/accounting', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        // image_url: '/icons/bell.svg',
                        title: 'Task Copilot',
                        body: 'Automates task creation and syncs updates back to the source.',
                        category: 'TASKS',
                        button: { link: 'https://unified.to/crm', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Analysis sports activities',
                        body: 'Analyze sports club member activity (eg. Strava)',
                        category: 'CLUBS',
                        button: { link: 'https://unified.to/clubs', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Analytics Copilot',
                        body: 'Analyze analytics data and generate insights.',
                        category: 'ANALYTICS',
                        button: { link: 'https://unified.to/analytics', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Goal Tracking Agent',
                        body: 'Create and track employee goals, milestones, and progress.',
                        category: 'PERFORMANCE',
                        button: { link: 'https://unified.to/performance', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Feedback Copilot',
                        body: 'Create and sync employee feedback across performance systems.',
                        category: 'PERFORMANCE',
                        button: { link: 'https://unified.to/performance', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                    {
                        title: 'Performance Review Agent',
                        body: 'Pull performance reviews, ratings, and reviewer comments.',
                        category: 'PERFORMANCE',
                        button: { link: 'https://unified.to/performance', title: 'Learn More', icon: 'ic:round-keyboard-arrow-right', },
                    },
                ]" />
        </section>

        <Benefits bottom_image title="The most secure MCP server for production AI" :benefits="[
            {
                title: 'Zero data storage',
                body: 'Unified MCP never stores customer data. Each tool call retrieves data live from the source system and returns it in real time, without persistence or caching. Unified is not a database and does not retain customer records.',
            },
            {
                title: 'Explicit authorization, not implicit access',
                body: 'Every MCP session is tied to an authorized customer connection. Before any tool is available, an end user must complete an authorization flow that defines what data and actions are allowed. Agents cannot access data without this step—there is no ambient or assumed access.',
            },
            {
                title: 'Controlled tool availability',
                body: 'Tools made available through MCP reflect what the authorized connection allows. You can further narrow access by limiting which tools are enabled for a given agent or workflow. This keeps the agent’s execution surface small and predictable.',
            },
            {
                title: 'Strict connection isolation',
                body: 'Each MCP request is scoped to a single customer connection. There is no shared context, cross-tenant access, or privilege escalation. Agents operate only within the boundary you define.',
            },
            {
                title: 'Access Restrictions',
                body: 'Unified MCP can further restrict your customer\'s authorized connection permissions by limited object read/write access'
            },
            {
                title: 'PII Data Removal',
                body: 'Unified MCP can remove PII data from the response before it reaches the model.'
            }

        ]" />

        <Testimonials />

        <Hero title="One Integration. All actions." heading-level="h2" :highlightWords="['All', 'actions.']"
            :subtitle="`Access authorized SaaS data from ${INTEGRATIONS_COUNT_10s}+ integrations with 20,000+ callable tools inside a LLM API - with no custom integration logic or prompt hacking.`"
            bg :buttons="[{
                link: 'https://app.unified.to',
                title: 'Get started free',
            },
            {
                link: '/contact',
                title: 'Speak to an expert',
                icon: 'ic:round-keyboard-arrow-right',
            }]" />

    </div>
</template>

<script setup lang="ts">
import { INTEGRATIONS_COUNT_10s } from '~/services/integrations';

useSEO({
    title: 'Unified MCP',
    description: `Easily connect your AI application to ${INTEGRATIONS_COUNT_10s} real-time SaaS data sources with 20,000+ callable tools inside LLM APIs — no custom integration logic or prompt hacking.`,
});

</script>